import { t } from '@lingui/macro';
import { Button, FormControlLabel, Radio, RadioGroup, Typography, makeStyles } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import { useLocation } from 'react-router-dom';

import NetworkGraph from '../../network-graph/NetworkGraph';
import { PathVisualizationChart } from '../../path-visualization/PathVisualizationChart';
import { useAgent } from '../../filters';
import { PathVisualizationInfo } from '../../path-visualization/PathVisualizationInfo';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';
import { useId } from '../../page-framework/panel/Panel';
import { useNavigate } from '../../../utilities/PgmUtilities';

const useStyles = makeStyles(() => ({
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    radioWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    pathVisualizationWrapper: {
        width: '100%',
    },
    pathDataUnavailableWrapper: {
        height: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    detailedChartWrapper: {
        height: 400,
    },
}));

export function PathDetailVisualizationSection({ graphData, graphOptions, pathData, testData }) {
    const id = useId();
    const styles = useStyles();
    const agent = useAgent();
    const pageStateKey = `${id}-PathChart`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, 'detailed');
    const navigate = useNavigate();
    const location = useLocation();

    if (!pathData || !pathData?.path || pathData.path.length === 0) {
        return (
            <>
                <div className={styles.sectionHeader}>
                    <span>
                        {location.state?.fromTestLogPage && (
                            <Button startIcon={<ChevronLeft />} onClick={() => navigate(-1)}>
                                {t`Go Back`}
                            </Button>
                        )}
                    </span>
                </div>
                <div className={styles.pathDataUnavailableWrapper}>
                    <Typography>{t`Path data unavailable for this test run.`}</Typography>
                </div>
            </>
        );
    }

    // Set agent data on the path data for correct agent node generation in detailed path visualization
    pathData.agent = agent;

    // Add status message to target hop if it exists and is not successful
    if (pathData.summary?.status?.code !== 0) {
        pathData.path.at(-1).statusMessage = pathData.summary.status.message;
    }

    return (
        <>
            <div className={styles.sectionHeader}>
                <span>
                    {location.state?.fromTestLogPage && (
                        <Button startIcon={<ChevronLeft />} onClick={() => navigate(-1)}>
                            {t`Go Back`}
                        </Button>
                    )}
                </span>
                <RadioGroup
                    className={styles.radioWrapper}
                    row
                    value={chartDisplayType}
                    onChange={(e) => setChartDisplayType(e.target.value)}
                >
                    <FormControlLabel value="detailed" label={t`Full Path`} control={<Radio />} />
                    <FormControlLabel value="simple" label={t`ISP Path`} control={<Radio />} />
                </RadioGroup>
            </div>
            <div className={styles.pathVisualizationWrapper}>
                {chartDisplayType === 'simple' ? (
                    <NetworkGraph data={graphData} options={graphOptions} />
                ) : (
                    <>
                        <PathVisualizationInfo
                            pathDelay={pathData.summary.route.latency}
                            pathNumberOfHops={pathData.summary.route.hops}
                            statusMessage={pathData.summary.status.code !== 0 && pathData.summary.status.message}
                        />
                        <div className={styles.detailedChartWrapper}>
                            <PathVisualizationChart data={pathData} test={testData} />
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
